import { useState } from "react";
import { supabase } from "../lib/supabase";
import { navigate } from "../lib/router";
import { COPY } from "../content/copy";

type Step = "credentials" | "enroll" | "verify";

export function AdminLoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [step, setStep] = useState<Step>("credentials");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [enrollQrSvg, setEnrollQrSvg] = useState<string | null>(null);
  const [enrollSecret, setEnrollSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function beginChallenge(id: string): Promise<boolean> {
    const { data: chal, error: chalErr } = await supabase().auth.mfa.challenge({ factorId: id });
    if (chalErr || !chal) return false;
    setFactorId(id);
    setChallengeId(chal.id);
    return true;
  }

  async function beginEnrollment(): Promise<boolean> {
    // We only reach this path when no VERIFIED TOTP factor exists, so any
    // TOTP factor listFactors returns here is stale (half-enrolled, or
    // orphaned by a previous attempt). Sweep all of them serially before
    // calling enroll() - otherwise Supabase Auth returns
    // "mfa_factor_name_conflict" or refuses to enroll a second factor.
    const { data: existing } = await supabase().auth.mfa.listFactors();
    for (const f of existing?.totp ?? []) {
      await supabase().auth.mfa.unenroll({ factorId: f.id });
    }
    // Supabase enforces unique friendlyName per user (even for the empty
    // default), and orphaned factors that listFactors does not surface
    // can still block enroll(). Force a unique name per attempt with a
    // timestamp; the user only ever sees the `issuer` in their app.
    const { data, error: enrollErr } = await supabase().auth.mfa.enroll({
      factorType: "totp",
      issuer: "Bordeciel Map Admin",
      friendlyName: `admin-${Date.now()}`,
    });
    if (enrollErr || !data || data.type !== "totp") return false;
    setFactorId(data.id);
    setEnrollQrSvg(data.totp.qr_code);
    setEnrollSecret(data.totp.secret);
    return true;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (step === "credentials") {
        const { error: authErr } = await supabase().auth.signInWithPassword({ email, password });
        if (authErr) {
          setError(COPY.admin.login.error_generic.text);
          return;
        }
        const { data: factors } = await supabase().auth.mfa.listFactors();
        const verified = factors?.totp?.find((f: { status: string }) => f.status === "verified");
        if (verified) {
          const ok = await beginChallenge((verified as { id: string }).id);
          if (!ok) { setError(COPY.admin.login.error_generic.text); return; }
          setStep("verify");
          return;
        }
        // First-time enrollment for this admin.
        const enrolled = await beginEnrollment();
        if (!enrolled) { setError(COPY.admin.login.error_generic.text); return; }
        setStep("enroll");
        return;
      }
      if (step === "enroll") {
        if (!factorId) { setError(COPY.admin.login.error_generic.text); return; }
        const { data: chal, error: chalErr } = await supabase().auth.mfa.challenge({ factorId });
        if (chalErr || !chal) { setError(COPY.admin.login.error_generic.text); return; }
        const { error: verifyErr } = await supabase().auth.mfa.verify({
          factorId,
          challengeId: chal.id,
          code: totp,
        });
        if (verifyErr) { setError(COPY.admin.login.error_generic.text); return; }
        navigate("/admin");
        return;
      }
      // step === "verify"
      if (!factorId || !challengeId) { setError(COPY.admin.login.error_generic.text); return; }
      const { error: verifyErr } = await supabase().auth.mfa.verify({
        factorId,
        challengeId,
        code: totp,
      });
      if (verifyErr) { setError(COPY.admin.login.error_generic.text); return; }
      navigate("/admin");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="landing__form" onSubmit={submit}>
      <h1 className="landing__heading">
        {step === "enroll" ? COPY.admin.login.enroll_heading.text : COPY.admin.login.title.text}
      </h1>
      {step === "credentials" && (
        <>
          <input
            type="email"
            className="landing__input"
            placeholder={COPY.admin.login.email.text}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            autoComplete="username"
          />
          <input
            type="password"
            className="landing__input"
            placeholder={COPY.admin.login.password.text}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </>
      )}
      {step === "enroll" && (
        <>
          <p className="landing__hint">{COPY.admin.login.enroll_hint.text}</p>
          {enrollQrSvg && (
            // Supabase returns totp.qr_code as a `data:image/svg+xml;utf-8,...`
            // URI, so it must go through <img src>, not innerHTML. White
            // background + generous size because many authenticator apps
            // refuse to scan small or low-contrast codes on dark themes.
            <img
              className="landing__qr"
              src={enrollQrSvg}
              alt=""
              style={{
                background: "#fff",
                padding: "12px",
                width: "220px",
                height: "220px",
                display: "block",
              }}
            />
          )}
          {enrollSecret && (
            <>
              <p className="landing__hint">{COPY.admin.login.enroll_manual_hint.text}</p>
              <code className="landing__secret" style={{ userSelect: "all", wordBreak: "break-all" }}>
                {enrollSecret}
              </code>
            </>
          )}
          <input
            type="text"
            className="landing__input"
            placeholder={COPY.admin.login.totp.text}
            value={totp}
            onChange={(e) => setTotp(e.target.value)}
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus
          />
        </>
      )}
      {step === "verify" && (
        <input
          type="text"
          className="landing__input"
          placeholder={COPY.admin.login.totp.text}
          value={totp}
          onChange={(e) => setTotp(e.target.value)}
          inputMode="numeric"
          pattern="[0-9]*"
          autoFocus
        />
      )}
      <button type="submit" className="landing__submit" disabled={busy}>
        {COPY.admin.login.submit.text}
      </button>
      <p className="landing__error" role="alert">
        {error ?? " "}
      </p>
    </form>
  );
}
